'use client';

import { storyblokEditable } from '@storyblok/react';
import { easeOut, motion } from 'framer-motion';

import Container from '@/components/elements/Container';
import Project, {
  ListingOrientation,
} from '@/components/elements/Features/Projects';
import { useArticles } from '@/components/elements/ListingProvider';
import PreHeading from '@/components/elements/PreHeading';
import Section from '@/components/elements/Section';

const TYPE_MAP = {
  project: 'project',
  insight: 'storyPost',
  story: 'storyPost',
};

const TYPE_LABEL = {
  project: 'Projects',
  insight: 'Insights',
  story: 'Stories',
};

const variants = {
  active: {
    y: '0px',
    opacity: 1,
  },
  hidden: {
    y: '-10px',
    opacity: 0,
  },
};

const AllProjects = ({ blok, articles }) => {
  return (
    <div>
      <div
        className="grid w-full grid-rows-1 gap-6"
        {...storyblokEditable(blok)}
      >
        {articles.map((article, i) => (
          <motion.div
            key={article.uuid + articles.length}
            variants={variants}
            initial="hidden"
            animate={'active'}
            transition={{
              delay: i * 0.1,
              ease: easeOut,
            }}
          >
            <Project
              {...article.content}
              orientation={ListingOrientation.HORIZONTAL}
              full_slug={article.full_slug}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const AllInsights = ({ blok, articles }) => {
  return (
    <div>
      <div className="mb-16">
        {articles[0] && (
          <Project {...articles[0].content} full_slug={articles[0].full_slug} />
        )}
      </div>
      <div
        className="grid w-full grid-rows-1 gap-6"
        {...storyblokEditable(blok)}
      >
        {articles[1] &&
          articles
            .slice(1)
            .map((article) => (
              <Project
                key={article._uid}
                {...article.content}
                orientation={ListingOrientation.HORIZONTAL}
                full_slug={article.full_slug}
              />
            ))}
      </div>
    </div>
  );
};

export default function ArticleListings({ blok }) {
  const { loading, selectedTags, clearTags, articles, tags, toggleTag } =
    useArticles(TYPE_MAP[blok.content_type]);

  return (
    <Section>
      <Container
        size="widest"
        className="grid grid-cols-1 gap-8 md:grid-cols-8 md:gap-12 xl:gap-16"
      >
        <div className="md:col-span-3 xl:col-span-2">
          <div className="top-16 self-start md:sticky">
            {!loading && (
              <div className="space-y-2">
                <PreHeading size="base" color="gray">
                  Categories
                </PreHeading>
                <ul className="space-y-2">
                  <li onClick={() => clearTags()}>
                    <div
                      className={`inline-flex items-center gap-4 rounded-full px-6 py-3.5 ${
                        selectedTags.length > 0
                          ? 'bg-theme-offwhite'
                          : 'bg-theme-black text-white'
                      } cursor-pointer`}
                    >
                      <span
                        className={`block size-2 rounded-full duration-200 ${
                          selectedTags.length > 0
                            ? '-ml-6 bg-transparent'
                            : 'bg-theme-primary'
                        }`}
                      ></span>
                      <span className="relative top-px align-baseline text-lg leading-none">
                        All {TYPE_LABEL[blok.content_type]}
                      </span>
                    </div>
                  </li>
                  {tags.map((v) => (
                    <li key={v} onClick={() => toggleTag(v)}>
                      <span
                        className={`inline-flex items-center gap-4 rounded-full px-6 py-3.5 ${
                          selectedTags.includes(v)
                            ? 'bg-theme-black text-white'
                            : 'bg-theme-offwhite'
                        } cursor-pointer`}
                      >
                        <span
                          className={`block size-2 rounded-full duration-200 ${
                            selectedTags.includes(v)
                              ? 'bg-theme-primary'
                              : '-ml-6 bg-transparent'
                          }`}
                        ></span>
                        <span className="relative top-px align-baseline text-lg leading-none">
                          {v}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        {!loading && (
          <div className="md:col-span-5 xl:col-span-6">
            {blok.content_type === 'project' && (
              <AllProjects blok={blok} articles={articles} />
            )}
            {blok.content_type === 'insight' && (
              <AllInsights blok={blok} articles={articles} />
            )}
          </div>
        )}
      </Container>
    </Section>
  );
}
